import { ref, h, createApp, defineComponent, App } from 'vue';

// COMPONENTS
import { GConfigProvider } from '@flash-global66/g-config-provider';
import Pin from '../views/Pin/index.vue'

// CONFIG
import VueReCaptcha from '@/lib/vue-recaptcha';
import { pinia } from '@/stores';

import router from '@/router';

// TYPES
import { PinType, IShowPin } from '../types/pin.type';

const show = ref(false);
const error = ref('');
const title = ref<string>('');
const description = ref<string>('');
const fromSignIn = ref<boolean>(false);
const type = ref<PinType>(PinType.VALIDATE);
const onNext = ref<(pin: string) => Promise<void>>(async () => {});
const onForgotPin = ref<() => Promise<void>>(async () => {});

const appInstance = ref<App<Element> | null>(null); // Almacena la instancia de la app
const containerInstance = ref<HTMLElement | null>(null); // Almacena el contenedor

export const usePinFlow = () => {
  /**
   * Configura el flujo de PIN antes de su visualización.
   * Este método debe invocarse antes de `showPin()` y limpia cualquier estado previo para garantizar un inicio limpio.
   *
   * @param {IShowPin} config - Objeto de configuración para el flujo.
   * @param {PinType} config.pinType - Define el tipo de flujo a mostrar ('PUT', 'CREATE', 'RESET', 'CHANGE').
   * @param {string} [config.pinTitle] - Título personalizado para la pantalla del PIN.
   * @param {string} [config.pinDescription] - Descripción personalizada para la pantalla del PIN.
   * @param {string} [config.pinButtonPrimary] - Texto para el botón de acción principal.
   * @param {boolean} [config.isSignIn=false] - Indica si el flujo se invoca desde el login para adaptar la UI.
   */
  const setFlowConfig = (config: IShowPin) => {
    cleanseState();

    type.value = config.pinType;
    title.value = config.pinTitle || '';
    description.value = config.pinDescription || '';
    fromSignIn.value = config.isSignIn || false;
  };

  /**
   * Define la función callback que se ejecutará cuando el usuario complete exitosamente el flujo.
   *
   * @param {(pin: string) => Promise<void>} method - La función asíncrona a ejecutar. Recibe el PIN de 4 dígitos como un string.
   */
  const setOnNext = (method: (pin: string) => Promise<void>) => {
    onNext.value = method;
  };

  /**
   * Crea y monta una nueva instancia de la "mini-app" de PIN en el DOM.
   * Este método lanza la interfaz de usuario modal a pantalla completa y debe ser llamado después de `setFlowConfig()`.
   *
   * @returns {Promise<boolean>} Una promesa que se resuelve a `true` cuando el componente se cierra, ya sea por éxito, cancelación o programáticamente.
   */
  const showPin = () => {
    show.value = true;

    return new Promise((resolve) => {
      let closed = false;

      const container = document.createElement('div');
      document.body.appendChild(container);

      const onClose = () => {
        if (closed) return;
        closed = true;

        hidePin();

        resolve(true);
      };

      /**
       * @description Wrapper crea una instancia de Vue limitada que se monta en un contenedor insertado en el DOM.
       */
      const Wrapper = defineComponent({
        name: 'Pin',
        setup() {
          return () =>
            h(GConfigProvider, null, {
              default: () =>
                h(Pin, {
                  onClose,
                }),
            });
        },
      });

      const app = createApp(Wrapper);
      app.use(router);
      app.use(pinia);
      VueReCaptcha(app);

      appInstance.value = app;
      containerInstance.value = container;

      app.mount(container);
    });
  };

  /**
   * Define la función callback que se ejecutará si el usuario inicia el flujo de "Olvidé mi PIN".
   *
   * @param {() => Promise<void>} method - La función asíncrona a ejecutar.
   */
  const setOnForgotPin = (method: () => Promise<void>) => {
    onForgotPin.value = method;
  };

  /**
   * Asigna un mensaje de error para ser mostrado en la interfaz del componente PIN.
   *
   * @param {string} err - El mensaje de error a mostrar, típicamente en el input del PIN.
   */
  const setPinError = (err) => {
    error.value = err;
  };

  /**
   * Cierra y destruye la instancia del componente PIN de forma programática.
   * Se encarga de la limpieza completa del estado del composable y de los elementos del DOM.
   */
  const hidePin = () => {
    cleanseState();
  };

  const cleanseState = () => {
    show.value = false;
    error.value = '';
    title.value = '';
    description.value = '';
    fromSignIn.value = false;
    type.value = PinType.VALIDATE;
    onNext.value = async () => {};
    onForgotPin.value = async () => {};
    
    checkAndDestroyAppInstance();
  };

  const checkAndDestroyAppInstance = () => {
    if (appInstance.value) {
      appInstance.value.unmount();
      appInstance.value = null;
    }
    if (containerInstance.value && containerInstance.value.parentNode) {
      containerInstance.value.parentNode.removeChild(containerInstance.value as HTMLElement);
      containerInstance.value = null;
    }
  };

  return {
    setFlowConfig,
    setOnNext,
    setOnForgotPin,
    showPin,
    setPinError,
    hidePin,
    show,
    error,
    title,
    description,
    fromSignIn,
    type,
    onNext,
    onForgotPin,
  };
};
